import React, { ComponentProps } from 'react';
import { SelectionPanel } from './components/SelectionPanel';
import { SelectionPanelGroupTheme } from './common/SelectionPanelGroupTheme';
import { SelectionPanelBorderRadius } from './common/SelectionPanelBorderRadius';
import { SelectionPanelDropShadowType } from './common/SelectionPanelDropShadowType';
import { SelectionPanelGroupStyleMode } from './common/SelectionPanelGroupStyleMode';
import { SelectionPanelCheckShape } from './common/SelectionPanelCheckShape';
import { SelectionPanelCheckStyle } from './common/SelectionPanelCheckStyle';
import { SelectionPanelGroupLayoutStyle } from './common/SelectionPanelGroupLayoutStyle';
import { SelectionPanelGroupComponentSpacing } from './common/SelectionPanelGroupComponentSpacing';
import { SelectionPanelGroupSelectionType } from './common/SelectionPanelGroupSelectionType';
import { InfoIconButton } from '../InfoIconButton';
import '../../../scss/controls/_selectionpanelgroup.scss';

export type SelectionPanelProps = ComponentProps<typeof SelectionPanel>;

/** Everything about how the group looks and is laid out (no items or selection). */
export interface SelectionPanelGroupBaseProps {
    id: string;
    theme: SelectionPanelGroupTheme;
    /** Card corner radius (Enhanced only; Condensed has no card). Defaults to None. */
    borderRadius?: SelectionPanelBorderRadius;
    /** Card shadow (Enhanced only; Condensed has no card). Defaults to None. */
    dropShadowType?: SelectionPanelDropShadowType;
    wrapPanels?: boolean;
    panelWidth?: string; // Only applicable for wrapped panels
    /** Total width of the component (label included, when there is one). Defaults to auto. */
    width?: string;
    height?: string;
    display?: string;
    gap?: string;
    /** Enhanced (default): the original bordered panels. Condensed: a compact checklist. */
    styleMode?: SelectionPanelGroupStyleMode;
    /** Circle or Square check indicator. When omitted: Circle for Enhanced, Square for Condensed. */
    checkShape?: SelectionPanelCheckShape;
    /** Checkmark (default): filled indicator with a check mark. Fill: radio-style dot/square inside the outline. */
    checkStyle?: SelectionPanelCheckStyle;
    /** Optional label for the group. */
    label?: string;
    /** Label above the panels (Vertical, default) or beside them (Horizontal). */
    layoutStyle?: SelectionPanelGroupLayoutStyle;
    /** Width of the label column when layoutStyle is Horizontal. Defaults to 120px. Ignored for Vertical. */
    labelWidth?: string;
    /** Adds " *" after the label (only when there is a label). Defaults to false. */
    required?: boolean;
    showInfoIcon?: boolean;
    infoText?: React.ReactNode;
    /** Vertical margin around the whole component. Omit for no margin (the original behaviour). */
    componentSpacing?: SelectionPanelGroupComponentSpacing;
}

/** The base props plus the items and the selection. */
export interface SelectionPanelGroupProps extends SelectionPanelGroupBaseProps {
    items: SelectionPanelProps[];
    selectedPanels: string[];
    set_selectedPanels: (selections: string[]) => void;
    selectionType?: SelectionPanelGroupSelectionType;
    maxSelection?: number;
}

export const SelectionPanelGroup: React.FC<SelectionPanelGroupProps> = ({
    id,
    items,
    theme,
    borderRadius,
    dropShadowType,
    selectedPanels,
    set_selectedPanels,
    selectionType = SelectionPanelGroupSelectionType.Single,
    wrapPanels = false,
    panelWidth,
    maxSelection,
    width = 'auto',
    height = 'auto',
    display,
    gap,
    styleMode = SelectionPanelGroupStyleMode.Enhanced,
    checkShape,
    checkStyle = SelectionPanelCheckStyle.Checkmark,
    label,
    layoutStyle = SelectionPanelGroupLayoutStyle.Vertical,
    labelWidth,
    required = false,
    showInfoIcon = false,
    infoText,
    componentSpacing,
}) => {
    const isHorizontal = layoutStyle === SelectionPanelGroupLayoutStyle.Horizontal;
    const isCondensed = styleMode === SelectionPanelGroupStyleMode.Condensed;
    // With a label (or Horizontal) the group sits inside a wrapper; the wrapper then takes `width` and the panels fill it.
    const hasWrapper = !!label || isHorizontal;
    // The spacing class goes on the outermost element (the wrapper when there is one); no class when not passed.
    const spacingClass = componentSpacing ? ` ${componentSpacing}` : '';

    const setStyle = {
        width: hasWrapper && width !== 'auto' ? '100%' : `${width}`,
        height: `${height}`,
        display: `${display}`,
        gap: `${gap}`,
        '--itemCount': `${items.length}`,
    };

    const group = (
        <div
            id={`selectionPanelGroup_${id}`}
            className={`selectionPanelGroup ${wrapPanels ? 'wrapped' : 'unwrapped'}${isCondensed ? ' condensed' : ''}${hasWrapper ? '' : spacingClass}`}
            style={setStyle}
        >
            {items?.map((panel) => (
                <SelectionPanel
                    key={String(panel.id)}
                    id={panel.id}
                    borderRadius={borderRadius}
                    dropShadowType={dropShadowType}
                    selectedPanels={selectedPanels}
                    set_selectedPanels={set_selectedPanels}
                    handleClick={panel.handleClick}
                    enabled={panel.enabled}
                    theme={theme}
                    wrapPanels={wrapPanels}
                    panelWidth={panelWidth}
                    selectionType={selectionType}
                    maxSelection={maxSelection}
                    styleMode={styleMode}
                    checkShape={checkShape}
                    checkStyle={checkStyle}
                >
                    {panel.children}
                </SelectionPanel>
            ))}
        </div>
    );

    // No label and the default layout: render the group on its own, exactly as before.
    if (!hasWrapper) {
        return <>{group}</>;
    }

    return (
        <div
            className={`selectionPanelGroup-container${isHorizontal ? ' horizontal' : ''}${isCondensed ? ' condensed' : ''}${spacingClass}`}
            style={
                {
                    ...(width !== 'auto' ? { width } : {}),
                    ...(isHorizontal ? { '--selectionPanelGroup-label-width': labelWidth ?? '120px' } : {}),
                } as React.CSSProperties
            }
        >
            {/* In Horizontal the label cell is always rendered, so the panels stay in the second column even without a label. */}
            {(label || isHorizontal) && (
                <div className='selectionPanelGroup-label'>
                    {label}
                    {label && required ? ' *' : ''}
                    {label && showInfoIcon && <InfoIconButton modalText={infoText} />}
                </div>
            )}
            {group}
        </div>
    );
};
